import type { CustomerPortalRpcClient } from "@redotech/customer-portal-sdk/rpc/client";
import { getKey } from "@redotech/react-util/key";
import { LoadState, useLoad, useTriggerLoad } from "@redotech/react-util/load";
import {
  DraftReturn,
  ShipmentMethodName,
} from "@redotech/redo-model/draft-return/draft-return";
import { getShipmentMethod } from "@redotech/redo-model/draft-return/util";
import {
  InteractiveMapLocation,
  parseAddressAsInteractiveMapLocation,
} from "@redotech/redo-model/interactive-map-location";
import { Button, ButtonTheme } from "@redotech/redo-web/button";
import { Flex } from "@redotech/redo-web/flex";
import BuildingIcon from "@redotech/redo-web/icon-old/building-02.svg";
import { LoadingRedoAnimation } from "@redotech/redo-web/loading-redo-animation";
import { Modal } from "@redotech/redo-web/modal";
import { Text } from "@redotech/redo-web/text";
import { memo, useContext, useMemo, useState } from "react";
import { SettingsContext } from "../../../contexts/settings";
import { InteractiveMap } from "../../components/interactive-map";
import { ShipmentMethodCard } from "./shipment-method-card";
import * as shipmentMethodCardsCss from "./shipment-method-cards.module.css";

export const ReturnInStoreCard = memo(function ReturnInStoreCard({
  draftReturn,
  setDraftReturn,
  client,
}: {
  draftReturn: DraftReturn;
  setDraftReturn(draftReturn: DraftReturn): void;
  client: CustomerPortalRpcClient;
}) {
  const [mapModalOpen, setMapModalOpen] = useState(false);

  const shipmentMethod = getShipmentMethod(draftReturn);

  const [setToInStoreLoad, setToInStore] = useTriggerLoad(async (signal) => {
    try {
      const response = await client.setShipmentMethodInfo(
        {
          draftReturnId: draftReturn._id,
          shipmentMethod: { method: ShipmentMethodName.IN_STORE },
        },
        { signal },
      );
      setDraftReturn(response.draftReturn);
    } catch (error) {
      console.error(error);
    }
  });

  const icon = useMemo(
    () => (
      <Flex height="full">
        <BuildingIcon />
      </Flex>
    ),
    [],
  );
  const title = useMemo(
    () =>
      shipmentMethod === ShipmentMethodName.IN_STORE ? (
        <Text fontWeight="bold">You've chosen to return in-store!</Text>
      ) : (
        <Text fontSize="lg" fontWeight="bold">
          Want to return in-store?
        </Text>
      ),
    [shipmentMethod],
  );
  const description = useMemo(
    () =>
      shipmentMethod === ShipmentMethodName.IN_STORE ? (
        <Text>
          After you submit your return, you can bring your items into the store
          and save the time of shipping.
        </Text>
      ) : (
        <Text>
          You can chose to bring your items right into the store and save the
          time of shipping.
        </Text>
      ),
    [shipmentMethod],
  );
  const button = useMemo(
    () =>
      shipmentMethod === ShipmentMethodName.IN_STORE ? (
        <Button
          onClick={() => setMapModalOpen(true)}
          theme={ButtonTheme.OUTLINED}
        >
          See Available Locations
        </Button>
      ) : (
        <Button
          // bgColor="brand-secondary"
          className={shipmentMethodCardsCss.shipmentMethodCardButton}
          onClick={() => setToInStore()}
          pending={setToInStoreLoad.pending}
          theme={ButtonTheme.BRAND}
        >
          Return in-store
        </Button>
      ),
    [shipmentMethod, setToInStore, setToInStoreLoad.pending],
  );

  return (
    <>
      <ShipmentMethodCard
        button={button}
        description={description}
        icon={icon}
        title={title}
      />
      <Modal
        onClose={() => setMapModalOpen(false)}
        open={mapModalOpen}
        title="Find a store that accepts drop offs:"
      >
        <LocationsModal client={client} draftReturn={draftReturn} />
      </Modal>
    </>
  );
});

export const LocationsModal = memo(function LocationsModal({
  client,
  draftReturn,
  returnId,
}: {
  client: CustomerPortalRpcClient;
  draftReturn?: DraftReturn;
  returnId?: string;
}) {
  const settings = useContext(SettingsContext);
  const locationsLoad: LoadState<InteractiveMapLocation[]> =
    useLoad(async () => {
      const { locations } = await client.getLocations({
        draftReturnId: draftReturn?._id,
        returnId,
      });

      return [
        ...locations,
        ...(settings?.customInStoreAddresses?.map((address) =>
          parseAddressAsInteractiveMapLocation(address),
        ) ?? []),
      ];
    }, []);

  if (locationsLoad.pending) {
    return <LoadingRedoAnimation />;
  }
  if (locationsLoad.error) {
    return <Text textColor="error">Error loading locations</Text>;
  }
  if (!locationsLoad.value) {
    return null;
  }

  return (
    <InteractiveMap
      key={getKey(locationsLoad.value)}
      locations={locationsLoad.value}
    />
  );
});
